import { AdgroupFormData, BidStrategyType, CampaignFormData, OptimizeGoal, StrategyListItem } from '@tt4b/ttam-enums';
import { useAbVersionStore, useStdCampaignIs } from '@tt4b/ttam-hooks';
import { ComputedRef, Ref, computed } from 'vue-demi';
import { StrategyOption } from '../consts';
import { i18n } from './../../../../i18n';
import { Message } from 'byted-vui';

export const UnifiedBidStrategyType = {
  CPA: [BidStrategyType.BID_CAP, BidStrategyType.COST_CAP, BidStrategyType.LOWEST_COST],
  ROAS: [BidStrategyType.HIGHEST_VALUE, BidStrategyType.MIN_ROAS],
};

export function useLabel(props: { adgroupData: Ref<AdgroupFormData>; settableStrategy: ComputedRef<StrategyOption> }) {
  const { adgroupData, settableStrategy } = props;
  const label = computed(() => {
    if (!settableStrategy.value) {
      return '';
    }

    const isOptGoalClick = adgroupData.value.optimizeGoal === OptimizeGoal.CLICK;
    if (isOptGoalClick) {
      // TODO 编辑对存量数据的回显
      return settableStrategy.value.viewType === BidStrategyType.BID_CAP
        ? i18n.t('module_unified_bidding_title_bid_cap_click')
        : i18n.t('module_unified_bidding_title_cost_cap_click');
    } else {
      return i18n.t('module_unified_bidding_title_cost_cap');
    }
  });

  return {
    label,
  };
}

export function useLabelTip(props: {
  adgroupData: Ref<AdgroupFormData>;
  optional: ComputedRef<boolean>;
  settableStrategy: ComputedRef<StrategyOption>;
}) {
  const { optional, settableStrategy, adgroupData } = props;
  const labelTip = computed(() => {
    if (!settableStrategy.value) {
      return '';
    }
    const { optionalLabelTip, optionalForClickLabelTip, requireLabelTip, requireForClickLabelTip } =
      settableStrategy.value;
    const isOptGoalClick = adgroupData.value.optimizeGoal === OptimizeGoal.CLICK;

    if (isOptGoalClick) {
      return optional.value ? optionalForClickLabelTip : requireForClickLabelTip;
    } else {
      return optional.value ? optionalLabelTip : requireLabelTip;
    }
  });

  return {
    labelTip,
  };
}

export function useUnifiedBidStrategy(props: { campaignData: Ref<CampaignFormData> }) {
  const {
    isProductSalesCloseLoopUnifiedCpa,
    isProductSalesCloseLoopUnifiedRoas,
    isProductSalesOpenLoopUnifiedCpa,
    isProductSalesOpenLoopUnifiedRoas,
  } = useAbVersionStore();

  const { isProductSalesOpenLoop } = useStdCampaignIs({
    campaignFormData: props.campaignData,
  });

  const isUnifiedCpa = computed(() =>
    isProductSalesOpenLoop.value ? isProductSalesOpenLoopUnifiedCpa.value : isProductSalesCloseLoopUnifiedCpa.value,
  );
  const isUnifiedRoas = computed(() =>
    isProductSalesOpenLoop.value ? isProductSalesOpenLoopUnifiedRoas.value : isProductSalesCloseLoopUnifiedRoas.value,
  );
  return {
    isUnifiedCpa,
    isUnifiedRoas,
  };
}

export function useUnifiedBidStrategyVisible(props: {
  strategyList: Ref<StrategyListItem[]>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { campaignData, strategyList } = props;
  const { isUnifiedCpa, isUnifiedRoas } = useUnifiedBidStrategy({ campaignData });

  const isUnifiedCpaBidStrategyVisible = computed(() => {
    // 存量数据 VO 支持 Cost Cap 和 Highest Value，当作 Cost Cap 展示
    const hasCpaBidStrategy = strategyList.value.some(item => UnifiedBidStrategyType.CPA.includes(item.type));
    return hasCpaBidStrategy && isUnifiedCpa.value;
  });

  const isUnifiedRoasBidStrategyVisible = computed(() => {
    const isRoasBidStrategy = strategyList.value.every(item => UnifiedBidStrategyType.ROAS.includes(item.type));
    return isRoasBidStrategy && isUnifiedRoas.value;
  });
  return {
    isUnifiedCpaBidStrategyVisible,
    isUnifiedRoasBidStrategyVisible,
  };
}

export function displayMessageIfChangeBidStrategy({ preVal, curVal }: { preVal: number; curVal: number }) {
  const isValueToInput = !preVal && Boolean(curVal);
  const isValueToEmpty = Boolean(preVal) && !curVal;
  const isChangeStrategy = isValueToInput || isValueToEmpty;
  if (isChangeStrategy) {
    Message({
      message: `${i18n.t('module_unified_bidding_strategy_change_toast')}`,
      duration: 3000,
    });
  }
  return isChangeStrategy;
}
